import React, { useMemo, useState } from 'react';
import { useShare } from '../context/ShareContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const STATUS = {
  pending: { label: 'Anfrage offen', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Bestätigt', className: 'bg-emerald-100 text-emerald-800' },
  declined: { label: 'Abgelehnt', className: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Storniert', className: 'bg-gray-100 text-gray-700' },
  picked_up: { label: 'Abgeholt', className: 'bg-blue-100 text-blue-800' },
  expired: { label: 'Abgelaufen', className: 'bg-gray-100 text-gray-600' },
  no_show: { label: 'Nicht erschienen', className: 'bg-red-100 text-red-700' },
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
};

const ReservationPanel = ({ listing, chatId = null, compact = false }) => {
  const { user, users = [], reservations = [], createReservation, updateReservationStatus } = useShare();
  const [quantity, setQuantity] = useState(1);
  const [pickupAt, setPickupAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const relevant = useMemo(() => reservations.filter((item) => String(item.listingId) === String(listing?.id)), [reservations, listing?.id]);
  const active = relevant.filter((item) => ['pending', 'confirmed'].includes(item.status));
  const ownActive = active.find((item) => String(item.requesterId) === String(user?.id));
  const isProvider = String(listing?.providerId || listing?.provider_id) === String(user?.id);
  const available = Math.max(0, Number.parseInt(listing?.quantity, 10) || 0);

  if (!user || !listing || listing.isCommercial) return null;

  const run = async (callback) => {
    setBusy(true);
    try {
      await callback();
    } catch (error) {
      alert(error.message || 'Die Reservierung konnte nicht aktualisiert werden.');
    } finally {
      setBusy(false);
    }
  };

  const create = () => run(async () => {
    await createReservation({ listingId: listing.id, quantity, chatId, pickupAt: pickupAt || null, note });
    setNote('');
  });

  const changeStatus = (id, status) => run(() => updateReservationStatus(id, status));

  return (
    <section className={`${compact ? 'border-x border-gray-100 bg-white px-4 py-3' : 'rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5'} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-black text-gray-900">Reservierung</h2>
          <p className="text-xs text-gray-500">Mengen werden sofort vorgemerkt und bei Ablehnung oder Ablauf automatisch freigegeben.</p>
        </div>
        {!isProvider && available > 0 && !ownActive && <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-700">{available} verfügbar</span>}
      </div>

      {!isProvider && ownActive && (
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${STATUS[ownActive.status]?.className || 'bg-gray-100'}`}>{STATUS[ownActive.status]?.label || ownActive.status}</span>
              <p className="mt-2 font-bold text-gray-900">{ownActive.quantity} {ownActive.quantity === 1 ? 'Portion' : 'Portionen'}</p>
              {ownActive.pickupAt && <p className="mt-1 text-sm text-gray-500"><SafeIcon icon={FiIcons.FiClock} className="mr-1 inline" />{formatDateTime(ownActive.pickupAt)}</p>}
              {ownActive.note && <p className="mt-1 text-sm text-gray-500">{ownActive.note}</p>}
            </div>
            <button type="button" disabled={busy} onClick={() => changeStatus(ownActive.id, 'cancelled')} className="rounded-xl border border-red-200 px-3 py-2 text-sm font-bold text-red-600 disabled:opacity-50">Stornieren</button>
          </div>
        </div>
      )}

      {!isProvider && !ownActive && available > 0 && (
        <div className="grid gap-3 rounded-xl border border-gray-100 bg-white p-4 sm:grid-cols-2">
          <label className="text-sm font-bold text-gray-700">Portionen
            <input type="number" min="1" max={available} value={quantity} onChange={(event) => setQuantity(Math.max(1, Math.min(available, Number(event.target.value) || 1)))} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" />
          </label>
          <label className="text-sm font-bold text-gray-700">Gewünschte Abholung
            <input type="datetime-local" value={pickupAt} onChange={(event) => setPickupAt(event.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" />
          </label>
          <label className="text-sm font-bold text-gray-700 sm:col-span-2">Hinweis (optional)
            <input type="text" maxLength="300" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Zum Beispiel: Abholung gegen 18 Uhr" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" />
          </label>
          <button type="button" disabled={busy} onClick={create} className="rounded-xl bg-emerald-600 px-4 py-3 font-bold text-white disabled:opacity-50 sm:col-span-2">{busy ? 'Wird reserviert…' : 'Verbindlich reservieren'}</button>
        </div>
      )}

      {!isProvider && !ownActive && available === 0 && <p className="rounded-xl bg-white p-4 text-sm font-bold text-gray-500">Aktuell sind keine Portionen mehr frei.</p>}

      {isProvider && (
        <div className="space-y-3">
          {active.length === 0 ? <p className="rounded-xl bg-white p-4 text-sm text-gray-500">Keine offenen Reservierungen.</p> : active.map((reservation) => {
            const requester = users.find((entry) => String(entry.id) === String(reservation.requesterId));
            return (
              <div key={reservation.id} className="rounded-xl border border-gray-100 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <strong>{requester?.name || 'Interessent'}</strong>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS[reservation.status]?.className || 'bg-gray-100'}`}>{STATUS[reservation.status]?.label || reservation.status}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-700">{reservation.quantity} {reservation.quantity === 1 ? 'Portion' : 'Portionen'}</p>
                    {reservation.pickupAt && <p className="mt-1 text-xs text-gray-500"><SafeIcon icon={FiIcons.FiClock} className="mr-1 inline" />{formatDateTime(reservation.pickupAt)}</p>}
                    {reservation.note && <p className="mt-1 text-xs text-gray-500">{reservation.note}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {reservation.status === 'pending' && <>
                      <button type="button" disabled={busy} onClick={() => changeStatus(reservation.id, 'confirmed')} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Bestätigen</button>
                      <button type="button" disabled={busy} onClick={() => changeStatus(reservation.id, 'declined')} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-600 disabled:opacity-50">Ablehnen</button>
                    </>}
                    {reservation.status === 'confirmed' && <>
                      <button type="button" disabled={busy} onClick={() => changeStatus(reservation.id, 'picked_up')} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Abgeholt</button>
                      <button type="button" disabled={busy} onClick={() => changeStatus(reservation.id, 'no_show')} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-600 disabled:opacity-50">Nicht erschienen</button>
                    </>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ReservationPanel;
